import React, { useEffect, useState } from 'react';
import { adminServices } from '../services/AdminServices';

const ImageModal = ({ show, title, referenceNo, extraText, onClose, remarksLabel = 'Remarks' }) => {
  const [fileSrc, setFileSrc] = useState(null);
  const [contentType, setContentType] = useState('image/png');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchAttachment = async () => {
      if (!show || !referenceNo) {
        setFileSrc(null);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const resp = await adminServices.getData(`attachments/${referenceNo.substring(0,12)}`);
        const data = resp && resp.data ? resp.data : resp;

        let attachment = null;
        if (data.attachment) attachment = data.attachment;
        else if (data.attachment1) attachment = data.attachment1;
        else if (typeof data === 'string') attachment = data;

        if (!attachment) {
          setError('No attachment available');
          setFileSrc(null);
          return;
        }

        // If already data URI, use it and extract mime type
        if (attachment.startsWith('data:')) {
          setFileSrc(attachment);
          const mime = attachment.substring(5, attachment.indexOf(';')) || 'application/octet-stream';
          if (!cancelled) setContentType(mime);
        } else {
          // Heuristic: common PDF base64 prefix is 'JVBERi0' (base64 for '%PDF')
          const looksLikePdf = attachment.startsWith('JVBERi0') || attachment.includes('JVBERi0');
          if (looksLikePdf) {
            const src = `data:application/pdf;base64,${attachment}`;
            if (!cancelled) {
              setFileSrc(src);
              setContentType('application/pdf');
            }
          } else {
            // Default to image/png when mime not provided
            const src = `data:image/png;base64,${attachment}`;
            if (!cancelled) {
              setFileSrc(src);
              setContentType('image/png');
            }
          }
        }
      } catch (e) {
        console.error('Error fetching attachment', e);
        if (!cancelled) setError('Failed to load attachment');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAttachment();
    return () => { cancelled = true; };
  }, [show, referenceNo]);

  if (!show) return null;

  const isPdf = contentType === 'application/pdf' || (fileSrc && fileSrc.includes('application/pdf'));

  return (
    <div className="jmodal-overlay" style={overlayStyle}>
      <div className="jmodal-content card" style={contentStyle}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold mb-0">{title || 'Attachment'}</h5>
            <button className="btn-close" aria-label="Close" onClick={onClose}></button>
          </div>

          <div>
            {extraText && (
              <div className="mb-3">
                <p className="mb-1 fw-bold">{remarksLabel}</p>
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{extraText}</div>
              </div>
            )}

            <div style={{ minHeight: 200 }} className="d-flex justify-content-center align-items-center">
              {loading && <div>Loading...</div>}
              {!loading && error && <div className="text-danger">{error}</div>}
              {!loading && !error && fileSrc && (
                isPdf ? (
                  <div style={{ width: '100%', height: '65vh' }}>
                    <iframe
                      src={fileSrc}
                      title={title || referenceNo}
                      style={{ width: '100%', height: '100%', border: 'none' }}
                    >
                      <object data={fileSrc} type="application/pdf" width="100%" height="100%">
                        <p>
                          PDF preview not supported. <a href={fileSrc}>Download</a>.
                        </p>
                      </object>
                    </iframe>
                  </div>
                ) : (
                  <img src={fileSrc} alt={title || referenceNo} style={{ maxWidth: '100%', maxHeight: '65vh' }} />
                )
              )}
            </div>
          </div>

          <div className="d-flex justify-content-end mt-3">
            <a href={fileSrc} download={title || referenceNo} className="btn btn-sm btn-outline-secondary me-2">Download</a>
            <button className="btn btn-sm btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const contentStyle = {
  width: '80%',
  maxWidth: '900px',
  borderRadius: 6,
  overflow: 'hidden'
};

export default ImageModal;